/**
 * Shared token-refresh single-flight.
 *
 * Every axios client in the app delegates token freshness to THIS module so
 * there is exactly ONE in-flight /auth/refresh at any moment, app-wide.
 *
 * Previously each client held its own module-level `refreshPromise`, so two
 * concurrent requests issued through different clients could each fire
 * /auth/refresh with the same *rotating* refresh token. The second call then
 * presented an already-consumed refresh token, got a 401, and raced the user
 * into a spurious logout (token thrash). Sharing the promise here collapses
 * those to a single refresh whose result every caller awaits.
 *
 * All clients read/write the SAME localStorage keys (the `bfs_*` keys defined
 * in shared/api/config.js AUTH_CONFIG, which are identical to the
 * `${APP_STORAGE_PREFIX}_*` keys used elsewhere), so this module is a safe,
 * drop-in replacement for each client's private copy.
 */
import axios from 'axios'
import { AUTH_CONFIG, API_CONFIG } from './config'
import {
  getAccessToken, setAccessToken, clearAccessToken, bootstrap, withRefreshLock,
  markRefreshInconclusive, refusalEndsSession,
} from './tokenStore'
// ONE policy, shared with useSessionTimeout. The idle window used to be written down here a second
// time, as a bare `26 * 60 * 1000` literal, and a second copy of a number is a second policy: making
// the window per-tenant while this stayed a literal would have produced a session the timer ends at
// eleven minutes while this file was still extending the token.
import { idleVerdict } from './sessionPolicy'

const REFRESH_TOKEN_KEY = AUTH_CONFIG.REFRESH_TOKEN_KEY // 'bfs_refresh_token'
const USER_KEY = AUTH_CONFIG.USER_KEY                 // 'bfs_user'
const IDENTITY_URL = API_CONFIG.IDENTITY.baseURL

/** Does this browser believe it holds a session? Same durable signal `useSessionTimeout` gates on. */
function hasSession() {
  return !!localStorage.getItem(USER_KEY)
}

// Decode a JWT payload without verifying the signature (just base64).
export function decodeTokenPayload(token) {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) return null
    return JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')))
  } catch {
    return null
  }
}

// The single shared in-flight refresh promise (null when no refresh is running).
let refreshPromise = null

/**
 * Return a fresh access token, proactively refreshing when it is within 2
 * minutes of expiry. Guarantees only one /auth/refresh runs at a time across
 * every client that calls this function.
 *
 * @returns {Promise<string|null>} a valid token, or null to force re-login.
 */
export async function ensureFreshToken() {
  // THE IDLE GATE COMES FIRST NOW, and that is a fix rather than a tidy-up.
  //
  // It used to sit BELOW the bootstrap early-return, which made it unreachable on the one path that
  // most needed it: whenever there is no in-memory access token — every page load, and every moment
  // straight after an idle logout, which calls `clearAccessToken()` — this function bootstrapped a
  // fresh token from the surviving refresh cookie and returned, without once looking at how long the
  // person had been gone. The one brake that could refuse to extend a dead session was fitted behind
  // the wheel it was meant to stop.
  //
  // Only enforced when this browser believes it holds a session: a logged-out visitor has no clock
  // to read, and blocking their bootstrap would be a different bug.
  if (hasSession()) {
    const verdict = idleVerdict()
    if (verdict === 'expired' || verdict === 'unknown') {
      // Stale, or of unknown age. Do not mint or extend anything — let the session timeout hook end
      // it. Returning the current token (usually null) makes the caller send an unauthenticated
      // request, which is the honest outcome for a session that is over.
      //
      // A session that is over is a verdict, whatever an earlier refresh met: clear the "no verdict"
      // flag, or a 429 from before the person walked away would stop the 401 that follows from
      // signing out a session the idle rule has already ended.
      markRefreshInconclusive(false)
      return getAccessToken()
    }
  }

  // Phase B3: the access token is in memory, so after a reload there is none even though the
  // session is perfectly valid (the refresh cookie survived). Mint one from the cookie rather
  // than returning null — returning null here would log out every reload.
  let token = getAccessToken()
  if (!token) {
    token = await bootstrap()
    return token
  }

  const payload = decodeTokenPayload(token)
  if (!payload || !payload.exp) return token

  const now = Math.floor(Date.now() / 1000)
  const timeLeft = payload.exp - now
  if (timeLeft > 120) return token

  // Access token fully expired — the refresh token (now an httpOnly cookie, or a legacy localStorage
  // copy for pre-migration sessions) is required. We CANNOT read the httpOnly cookie from JS, so we must
  // NOT pre-gate on a localStorage refresh token here (that would force a needless re-login for every
  // cookie-only session). Always attempt the refresh below; it 401s if there is genuinely no valid token.

  if (!refreshPromise) {
    // Cross-tab serialization — the missing half of this module's guarantee.
    // `refreshPromise` above is MODULE-level, i.e. per-tab: it collapses concurrent refreshes within
    // one tab but does nothing across tabs, and every tab of the origin shares ONE rotating refresh
    // cookie. `withRefreshLock` is the cross-tab mutex tokenStore already defines; until now
    // `bootstrap()` was its only caller, so two tabs refreshing together both presented the same
    // cookie and the loser took a 401 (identity `auth.py` blacklists the presented jti on rotation).
    // Held sequentially, the second tab simply presents the already-rotated cookie and succeeds.
    //
    // SCOPE THIS CAREFULLY: wrap ONLY this body. `bootstrap()` — called near the top of
    // ensureFreshToken — takes the SAME lock, and Web Locks are NOT reentrant, so wrapping the whole
    // function would deadlock every session that starts without a token, which is all of them.
    refreshPromise = withRefreshLock(async () => {
      try {
        // Phase B1: the refresh token lives in the httpOnly cookie, sent automatically via
        // withCredentials. Still send a legacy localStorage copy in the body for sessions that logged in
        // before the cookie existed. X-Requested-With satisfies the server CSRF check on cookie auth.
        const legacyRefresh = localStorage.getItem(REFRESH_TOKEN_KEY)
        const resp = await axios.post(
          `${IDENTITY_URL}/auth/refresh`,
          legacyRefresh ? { refresh_token: legacyRefresh } : null,
          { withCredentials: true, headers: { 'X-Requested-With': 'XMLHttpRequest' } },
        )
        const newToken = resp.data.access_token
        setAccessToken(newToken)
        // Phase B1: the rotated refresh token is now ONLY in the httpOnly cookie — never write it to
        // localStorage again, and purge any legacy copy so an XSS can no longer exfiltrate it.
        localStorage.removeItem(REFRESH_TOKEN_KEY)
        if (resp.data.user) {
          localStorage.setItem(USER_KEY, JSON.stringify(resp.data.user))
        }
        return newToken
      } catch (err) {
        // Refresh failed — clear tokens only if the access token is already expired; otherwise keep
        // the still-valid token (transient failure).
        //
        // Judge on the LIVE token, never the one captured before the await. A concurrent winner may
        // have installed a fresh token via setAccessToken() while this call was in flight; deciding
        // from the stale closure variable let a loser clearAccessToken() a perfectly valid token it
        // had never seen, which then sent the next request out with no Authorization header, took a
        // 401, and hard-redirected the user to /login mid-work.
        const current = getAccessToken()
        const p = decodeTokenPayload(current)
        const nowSec = Math.floor(Date.now() / 1000)
        if (!current || (p && p.exp && p.exp <= nowSec)) {
          clearAccessToken()
          localStorage.removeItem(REFRESH_TOKEN_KEY)
          // No usable token either way - but only a 401/403 says the session is over. A 429 or a
          // restart leaves it alive, and the shared client must not sign the person out over the
          // unauthenticated request that follows (tokenStore.refreshWasInconclusive).
          markRefreshInconclusive(!refusalEndsSession(err))
          return null
        }
        return current
      } finally {
        refreshPromise = null
      }
    })
  }
  return refreshPromise
}
